import React, { useCallback, useEffect, useState } from "react";
import { Container, Row, Col, Table, Button, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./Products.scss";
import { useAuthStore } from "../../../zustand/loginStore";
import {
  createAuthenticatedAxiosInstance,
  fetchCategories,
  fetchProducts,
  fetchSizes,
} from "api/auth";
import LoadingScreen from "components/general/LoadingScreen";
import { useProductStore } from "../../../zustand/productStore";
import { Product, ProductResponse } from "interfaces/IProduct";
import CustomNavbar from "components/users/CustomNavbar";
import SearchAndFilterBar from "components/general/SearchAndFilterBar";
import CustomSelect from "components/general/CustomSelect";
import CustomPagination from "components/general/CustomPagination";
import CustomBackButton from "components/general/CustomBackButton";
import { SearchTypes } from "types/SearchTypes";
import { isSuccessStatusCode } from "helpers/httpResponse";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";

const Products: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchType, setSearchType] = useState<SearchTypes>(
    "text"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOptions, setSearchOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const { token } = useAuthStore();
  const { setProductSelected, setSizes } = useProductStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [filterBy, setFilterBy] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [firstLoad, setFirstLoad] = useState(true);
  const [categories, setCategories] = useState<
    { value: string; label: string }[]
  >([]);

  const loadProducts = useCallback(async () => {
    const params: any = {
      page: currentPage,
      per_page: perPage,
    };

   if (searchTerm && filterBy) {
    if (filterBy.value === "category") {
      params.category_id = searchTerm;
    } else if (filterBy.value === "name" || filterBy.value === "brand") {
      params[filterBy.value] = searchTerm;
    } else if (filterBy.value === "created_at") {
      params.created_from = searchTerm;
      params.created_to = searchTerm;
    }
  }

    // Agregar filtro de status
    if (statusFilter) {
      if (statusFilter === "active") {
        params.status = 1;
      } else if (statusFilter === "inactive") {
        params.status = 0;
      } else if (statusFilter === "out_of_stock") {
        params.status = "agotados";
      }
    }

    const response: ProductResponse = await fetchProducts(
      axiosInstance,
      params
    );
    return response;
  }, [currentPage, perPage, filterBy, searchTerm, statusFilter, axiosInstance]);

  const loadAllSizes = useCallback(async () => {
    const { data, statusCode } = await fetchSizes(axiosInstance, {
      per_page: 1000,
      page: 1,
    });
    if(isSuccessStatusCode(statusCode)) 
    return data;
  }, [axiosInstance]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await fetchCategories(axiosInstance, {
        per_page: 1000,
        page: 1,
      });

      return response;
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [axiosInstance]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [productsResponse, sizesResponse, categoriesResponse] =
          await Promise.all([loadProducts(), loadAllSizes(), loadCategories()]);

        setProducts(productsResponse.products);
        setCurrentPage(productsResponse.current_page);
        setTotalPages(productsResponse.total_pages);

        sizesResponse && setSizes(sizesResponse?.tallas);
        const formattedCategories = categoriesResponse?.categories.map(
          (category) => ({
            value: category.id.toString(),
            label: category.name,
          })
        );
        setCategories(formattedCategories || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentPage, perPage]);

  /**
   * Effect hook to implement debounced search and filter
   * @effect
   */
  useEffect(() => {
    if (!firstLoad) {
      const delayDebounce = setTimeout(async () => {
        const productsResponse = await loadProducts();
        setProducts(productsResponse.products);
        setCurrentPage(productsResponse.current_page);
        setTotalPages(productsResponse.total_pages);
      }, 500);

      return () => {
        clearTimeout(delayDebounce);
      };
    } else {
      setFirstLoad(false);
    }
  }, [searchTerm, filterBy, statusFilter]);

  /**
   * Handles page change in pagination
   * @function handlePageChange
   * @param {number} page - The new page number
   */
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  /**
   * Handles search input change
   * @function handleSearch
   * @param {string} value - The change event
   */
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  /**
   * Handles filter change
   * @function handleFilterChange
   * @param {{ value: string; label: string } | null} newValue - The new filter value
   */
  const handleFilterChange = async (
    newValue: { value: string; label: string } | null
  ) => {
    setFilterBy(newValue);
    setCurrentPage(1);

    if (newValue && newValue.value === "category") {
      setSearchType("select");
      setSearchOptions(categories);
    } else {
      setSearchTerm("");
      setSearchType("text");
      setSearchOptions([]);
    }
  };

  /**
   * Handles status filter change
   * @function handleStatusFilterChange
   * @param {{ value: string; label: string } | null} newValue - The new status filter value
   */
  const handleStatusFilterChange = (newValue: { value: string; label: string } | null) => {
    setStatusFilterSelect(newValue);
    setStatusFilter(newValue?.value || "");
    setCurrentPage(1);
  };

  /**
   * Handles per page filter change
   * @function handlePerPageChange
   * @param {{ value: string; label: string } | null} newValue - The new per page value
   */
  const handlePerPageChange = (newValue: { value: string; label: string } | null) => {
    setPerPageSelect(newValue);
    setPerPage(parseInt(newValue?.value || "10"));
    setCurrentPage(1);
  };

  /**
   * Effect to sync perPageSelect with perPage state
   */
  useEffect(() => {
    const option = perPageOptions.find(opt => opt.value === perPage.toString());
    if (option) {
      setPerPageSelect(option);
    }
  }, [perPage]);

  /**
   * Handles row click in the product table
   * @function handleRowClick
   * @param {number} productId - The ID of the clicked product
   */
  const handleRowClick = (productId: number) => {
    setProductSelected(productId);
    navigate(`/products/${productId}`);
  };

  /**
   * Handles add product button click
   * @function handleAddProduct
   */
  const handleAddProduct = () => {
    navigate("/inventories/products/add");
  };

  /**
   * Filter options for the product table
   * @constant {Array<{value: string, label: string}>}
   */
  const filterOptions = [
    { value: "name", label: "Nombre" },
    { value: "category", label: "Categoría" },
    { value: "brand", label: "Marca" },
    { value: "created_at", label: "Fecha de creación" },
  ];

  /**
   * Status filter options for the product table
   * @constant {Array<{value: string, label: string}>}
   */
  const statusFilterOptions = [
    { value: "", label: "Todos los estados" },
    { value: "active", label: "Activos" },
    { value: "inactive", label: "Inactivos" },
    { value: "out_of_stock", label: "Agotados" },
  ];

  /**
   * Per page options for pagination
   * @constant {Array<{value: string, label: string}>}
   */
  const perPageOptions = [
    { value: "10", label: "10 por página" },
    { value: "25", label: "25 por página" },
    { value: "50", label: "50 por página" },
    { value: "100", label: "100 por página" },
  ];

  /**
   * Status filter state for CustomSelect
   */
  const [statusFilterSelect, setStatusFilterSelect] = useState<{ value: string; label: string } | null>(null);

  /**
   * Per page filter state for CustomSelect
   */
  const [perPageSelect, setPerPageSelect] = useState<{ value: string; label: string } | null>({ value: "10", label: "10 por página" });

  return (
    <>
      <CustomNavbar />
      <CustomBackButton onClick={() => navigate("/dashboard#inventarios")} />

      <Container className="px-4 px-md-5">
        <Row className="align-items-center mb-4 mt-5">
          <Col className="text-center mt-5">
            <h2 className="fw-bold">Inventario registrado</h2>
          </Col>
        </Row>
        {isLoading ? (
          <LoadingScreen loadingText="Cargando información del producto" />
        ) : (
          <>
            <SearchAndFilterBar
              searchType={searchType}
              searchTerm={searchTerm}
              onSearchChange={handleSearch}
              searchOptions={searchOptions}
              filterBy={filterBy}
              onFilterChange={handleFilterChange}
              filterOptions={filterOptions}
              isSearchDisabled={!filterBy}
            />
            {/* Status filter */}
            <div className="mb-3">
              <Row className="align-items-end">
                <Col xs={6} className="d-flex justify-content-start">
                  <div className="d-flex align-items-center w-75">
                    <InputGroup>
                      <Button
                        className="d-flex align-items-center bg-custom-yellow border-end-0"
                        style={{ border: "gray" }}
                      >
                        <FontAwesomeIcon icon={faFilter} style={{ color: "black" }} />
                      </Button>
                      <div className="filter-select-container">
                        <CustomSelect
                          options={statusFilterOptions}
                          value={statusFilterSelect}
                          onChange={handleStatusFilterChange}
                          placeholder="Filtrar por estado..."
                          isFilteredIconVisible={true}
                        />
                      </div>
                    </InputGroup>
                  </div>
                </Col>
                <Col xs={6} className="d-flex justify-content-end">
                  <div className="d-flex align-items-center w-75">
                    <div className="filter-select-container">
                      <CustomSelect
                        options={perPageOptions}
                        value={perPageSelect}
                        onChange={handlePerPageChange}
                        placeholder="Elementos por página..."
                        isFilteredIconVisible={false}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            {/* Search and filter components */}
            <div className="table-container mb-5">
              <Table striped bordered hover className=" border-radius-10">
                <thead className="bg-primary text-white">
                  <tr>
                    <th className="bg-custom-yellow text-center">Imagen</th>
                    <th className="bg-custom-yellow text-center">Nombre</th>
                    <th className="bg-custom-yellow text-center">Stock</th>
                    <th className="bg-custom-yellow text-center">Categoría</th>
                    <th className="bg-custom-yellow text-center">Precio</th>
                    <th className="bg-custom-yellow text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => (
                    <tr
                      key={product.id}
                      className={`custom-row ${
                        index % 2 === 0 ? "odd" : "even"
                      }`}
                      onClick={() => handleRowClick(product.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        <img
                          src={
                            product.attributes[0]?.images[0] ||
                            "placeholder-image-url"
                          }
                          alt={product.name}
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                          }}
                        />
                      </td>
                      <td className="text-center">
                        <div
                          style={{
                            maxHeight: "3em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {product.name}
                        </div>
                      </td>
                      <td className="text-center">{product.total_stock}</td>
                      <td className="text-center">
                        <div
                          style={{
                            maxHeight: "3em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {product.categories
                            .map((cat) => cat.category)
                            .join(", ")}
                        </div>
                      </td>
                      <td className="text-center">
                        {product.attributes[0]?.sizes[0]?.price || "N/A"}
                      </td>
                      <td className="text-center">
                        {(() => {
                          const isActive = product.status === 1;
                          const isOutOfStock = product.attributes.every(
                            (attr) =>
                              attr.sizes.every((size) => size.stock === 0)
                          );

                          if (!isActive) {
                            return (
                              <span className="badge badge-inactive">
                                Inactivo
                              </span>
                            );
                          } else if (isOutOfStock) {
                            return (
                              <span className="badge badge-out-of-stock">
                                Agotado
                              </span>
                            );
                          } else {
                            return (
                              <span className="badge badge-active">Activo</span>
                            );
                          }
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <Row className="justify-content-center mb-3">
              <Col xs="auto">
                <Button
                  variant="primary"
                  className=" fw-bold"
                  onClick={handleAddProduct}
                >
                  Agregar producto
                </Button>
              </Col>
            </Row>
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalResults={products.length}
            />
          </>
        )}
      </Container>
    </>
  );
};

export default Products;
